/* Ayudate · Página principal: estudiantes y tutores */

import React, { useState, useEffect } from "react";
import { ESTILOS } from "../estilos/global";
import { AREAS, CURSO, INSTITUCIONES } from "../datos/areas";
import { CONTACTO } from "../datos/contacto";
import { api } from "../api";
import { colones } from "../lib/formato";
import { precioDe, TARIFA_MINIMA, TARIFA_SUGERIDA, EJ, r100 } from "../lib/tarifas";
import { CatalogoTutores } from "../componentes/CatalogoTutores";
import { FormTutor } from "../componentes/FormTutor";
import { Faq } from "../componentes/Faq";
import { Marca } from "../componentes/Marca";
import { Accesibilidad, FiltrosColor } from "../componentes/Accesibilidad";
import { cargarA11y, guardarA11y, clasesA11y } from "../lib/a11y";
import { irA_ruta } from "../lib/rutas";
import { leerSesionEstudiante } from "../lib/sesionEstudiante";

export function SitioPublico({ modo = "estudiantes", origen = "/" }) {
  const esTutores = modo === "tutores";
  const esFaq = modo === "preguntas";
  const esNosotros = modo === "nosotros";
  const esSuelta = esFaq || esNosotros;   // páginas sin buscador ni registro
  const [activo, setActivo] = useState("");
  const [menuAbierto, setMenuAbierto] = useState(false);
  const [a11y, setA11y] = useState(cargarA11y);
  useEffect(() => { guardarA11y(a11y); }, [a11y]);
  const clasesPagina = [esTutores ? "pag-tutores" : "pag-estudiantes", clasesA11y(a11y)]
    .filter(Boolean).join(" ");
  const [tutores, setTutores] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [errorCarga, setErrorCarga] = useState("");

  /* Carga inicial desde la capa de datos. Sin actualización optimista: una
     tarjeta solo existe acá una vez que el backend la devuelve como
     aprobada — un registro recién enviado queda pendiente de revisión y no
     aparece en el sitio hasta que un administrador lo publique. */
  const cargar = React.useCallback(async () => {
    setCargando(true); setErrorCarga("");
    try {
      setTutores(await api.getTutores());
    } catch {
      setErrorCarga("No pudimos cargar los tutores. Revise su conexión y pruebe de nuevo.");
    } finally {
      setCargando(false);
    }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  /* Si hay un estudiante logueado (ver DashboardEstudiante.jsx), precarga
     nombre/correo/teléfono en el formulario de "Solicitar tutoría" (ver
     perfilInicial en Agenda.jsx) para que solo tenga que elegir día, hora
     y escribir la nota. Sin sesión, o si la sesión ya venció, sigue
     funcionando igual que para un visitante anónimo — este es el sitio
     público, no una ruta protegida, así que un error acá nunca debe
     interrumpir la navegación ni mandar a nadie a /login. */
  const [perfilEstudiante, setPerfilEstudiante] = useState(null);
  useEffect(() => {
    const sesion = leerSesionEstudiante();
    if (!sesion) return;
    let vivo = true;
    api.obtenerDashboardEstudiante(sesion.token)
      .then((r) => {
        if (vivo) setPerfilEstudiante({ nombre: r.nombre, correo: r.correo, telefono: r.telefono });
      })
      .catch(() => {});
    return () => { vivo = false; };
  }, []);
  const primerNombre = perfilEstudiante?.nombre?.trim().split(/\s+/)[0] || "";

  /* Buscador de la portada. Lo que se escribe arriba se le entrega al
     buscador de la sección, que es el que sabe filtrar: acá no hay lógica
     de búsqueda duplicada, solo el texto y un contador.

     El contador es lo que hace que buscar dos veces lo mismo vuelva a
     bajar y a re-aplicar el filtro: si se pasara el texto pelado, el
     segundo envío con la misma palabra no cambiaría la prop y el catálogo
     no se enteraría. */
  const [qPortada, setQPortada] = useState("");
  const [consulta, setConsulta] = useState(null);
  const buscarDesdePortada = (e) => {
    e.preventDefault();
    const texto = qPortada.trim();
    if (!texto) return;
    setConsulta((c) => ({ texto, n: (c?.n || 0) + 1 }));
    irA(e, "buscador");
  };

  // Desplazamiento suave a la sección, respetando prefers-reduced-motion
  const irA = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    const suave = !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    el.scrollIntoView({ behavior: suave ? "smooth" : "auto", block: "start" });
    el.setAttribute("tabindex", "-1");
    el.focus({ preventScroll: true });
    setActivo(id);
  };

  // Marca en el menú la sección que se está viendo
  useEffect(() => {
    const ids = ANCLAS.map((m) => m.id);
    const obs = new IntersectionObserver(
      (entradas) => {
        const visible = entradas
          .filter((x) => x.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visible) setActivo(visible.target.id);
      },
      /* El margen negativo de arriba es el alto de la barra fija (64 px)
         más el respiro del scroll-margin-top de las secciones. */
      { rootMargin: "-84px 0px -60% 0px", threshold: 0 }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, [modo]);

  const ANCLAS = esSuelta ? []
    : esTutores
      ? [{ id: "pasos", label: "Cómo funciona" },
         { id: "dar", label: "Para tutores" },
         { id: "tarifas", label: "Tarifas" },
         { id: "politicas", label: "Políticas" },
         { id: "registro", label: "Registro" }]
      : [{ id: "pasos", label: "Cómo funciona" },
         { id: "buscador", label: "Buscar tutor" },
         { id: "politicas", label: "Políticas" }];

  const rutaActual = esTutores ? "/tutores" : esFaq ? "/preguntas"
    : esNosotros ? "/nosotros" : "/";
  /* Al saltar entre páginas sueltas se conserva de dónde venía la persona */
  const desde = esSuelta ? origen : rutaActual;

  /* Preguntas y Quiénes somos viven en su propia página para no alargar las
     principales, pero no pesan igual: a Preguntas se va con una duda a
     medio camino de agendar, así que va en la barra de arriba, al lado de
     las secciones. Quiénes somos se lee una vez y no se vuelve — su lugar
     es el pie, que es donde se busca quién está detrás de un sitio.

     En las páginas sueltas la de arriba muestra las dos, porque ahí SON la
     navegación: sin secciones que anclar, la barra quedaría vacía y no
     habría cómo saltar de una a la otra. */
  const PAGINAS = esSuelta
    ? [{ ruta: "/preguntas", label: "Preguntas" },
       { ruta: "/nosotros", label: "Quiénes somos" }]
    : [{ ruta: "/preguntas", label: "Preguntas" }];

  const MENU = [...ANCLAS, ...PAGINAS];

  return (
    <div className={`v2 ${clasesPagina}`}>
      <style>{ESTILOS}</style>

      {/* Antes apuntaba a #buscador, que solo existe en la página de
          estudiantes: en /tutores, /preguntas y /nosotros el enlace no
          llevaba a ninguna parte. Ahora apunta al <main>, que sí está
          en las cuatro. */}
      <a className="salto" href="#contenido" onClick={(e) => irA(e, "contenido")}>
        Saltar al contenido principal
      </a>

      <FiltrosColor />

      <div className="contenido">
      <nav className="nav" aria-label="Principal">
        <div className="wrap nav-in">
          <a href="#top" className="logo" style={{ textDecoration: "none", color: "inherit" }}
            onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); setActivo(""); }}>
            {/* El isotipo y la palabra, en el mismo orden y con las
                mismas proporciones del manual (logos_y_estilos/). El
                símbolo se esconde de la accesibilidad porque no aporta
                nada que la palabra no diga ya; el sufijo es lo que hace
                que el enlace se anuncie como "Ayudate · Inicio" y no como
                un "Ayudate" suelto entre los demás del menú. */}
            <Marca sufijo="Inicio" />
          </a>
          <div className="nav-l">
            {MENU.map((m) => (
              m.ruta ? (
                <a key={m.ruta} href={m.ruta}
                  className={rutaActual === m.ruta ? "on" : ""}
                  aria-current={rutaActual === m.ruta ? "page" : undefined}
                  onClick={(e) => { e.preventDefault(); irA_ruta(m.ruta, desde); }}>{m.label}</a>
              ) : (
                <a key={m.id} href={`#${m.id}`} className={activo === m.id ? "on" : ""}
                  aria-current={activo === m.id ? "true" : undefined}
                  onClick={(e) => irA(e, m.id)}>{m.label}</a>
              )
            ))}
          </div>
          {esSuelta ? (
            <a href={origen} className="cambiar volver nav-cta"
              onClick={(e) => { e.preventDefault(); irA_ruta(origen); }}>
              <i aria-hidden="true">←</i>
              Volver a {origen === "/tutores" ? "dar tutorías" : "buscar tutor"}
            </a>
          ) : (
            <div className="nav-cta">
              {perfilEstudiante ? (
                <a href="/panelestudiante" className="cambiar"
                  onClick={(e) => { e.preventDefault(); irA_ruta("/panelestudiante"); }}>
                  Hola, {primerNombre}
                </a>
              ) : (
                <a href="/login" className="cambiar"
                  onClick={(e) => { e.preventDefault(); irA_ruta("/login", rutaActual); }}>
                  Iniciar sesión
                </a>
              )}
              <a href={esTutores ? "/" : "/tutores"} className="cambiar"
                onClick={(e) => { e.preventDefault(); irA_ruta(esTutores ? "/" : "/tutores"); }}>
                {esTutores ? "Buscar un tutor" : "Dar tutorías"}
                <i aria-hidden="true">→</i>
              </a>
            </div>
          )}
          <button type="button" className="hamburguesa" aria-label={menuAbierto ? "Cerrar menú" : "Abrir menú"}
            aria-expanded={menuAbierto} aria-controls="menu-movil"
            onClick={() => setMenuAbierto((v) => !v)}>
            <span aria-hidden="true">{menuAbierto ? "✕" : "☰"}</span>
          </button>
        </div>
      </nav>

      <div className={`nav-m-backdrop ${menuAbierto ? "abierto" : ""}`} onClick={() => setMenuAbierto(false)} />

      <div id="menu-movil" className={`nav-m ${menuAbierto ? "abierto" : ""}`} aria-label="Accesos rápidos">
        <div className="nav-m-in">
          {MENU.map((m) => (
            m.ruta ? (
              <a key={m.ruta} href={m.ruta}
                className={rutaActual === m.ruta ? "on" : ""}
                aria-current={rutaActual === m.ruta ? "page" : undefined}
                onClick={(e) => { e.preventDefault(); setMenuAbierto(false); irA_ruta(m.ruta, desde); }}>{m.label}</a>
            ) : (
              <a key={m.id} href={`#${m.id}`} className={activo === m.id ? "on" : ""}
                aria-current={activo === m.id ? "true" : undefined}
                onClick={(e) => { setMenuAbierto(false); irA(e, m.id); }}>{m.label}</a>
            )
          ))}
        </div>
      </div>

      <main id="contenido">

      {esSuelta ? (
        <header className="hero corto">
          <div className="wrap">
            <div className="eye">{esFaq ? "Preguntas y respuestas" : "Quiénes somos"}</div>
            <h1 className="anton chico">
              {esFaq ? "Lo que hay que saber antes de empezar." : "Dos estudiantes que viven el problema."}
            </h1>
            <p className="lede">
              {esFaq
                ? <>Si algo no está acá, escríbanos por WhatsApp y le respondemos <b>el mismo día</b>.</>
                : "Somos usuarios del problema que queremos resolver."}
            </p>
          </div>
        </header>
      ) : (
      <header className="hero">
        <div className="wrap">
          <p className="eye">
            {esTutores ? "Espacio para tutores" : "Tutorías estudiantiles privadas · Costa Rica"}
          </p>
          {/* Dos renglones, y el corte va donde lo pide la frase: el primero
              plantea el costo, el segundo lo compara. Dejarlo a que lo
              parta el ancho de la ventana lo volvía tres renglones en
              escritorio y rompía la comparación por la mitad. */}
          <h1 className="anton big">
            <span>Perder un curso <u>cuesta más</u></span>
            <span>que una tutoría.</span>
          </h1>
          {esTutores ? (
            <>
              <p className="lede">
                Usted ya aprobó ese curso que a otros se les está complicando. <b>Ponga su tarifa, su
                horario</b>, y nosotros le conseguimos los estudiantes.
              </p>
              <p className="frase">Gana con lo que ya sabe, sin salir del campus.</p>
              <div className="cta">
                <a href="#registro" className="btn btn-p" onClick={(e) => irA(e, "registro")}>
                  Registrarme como tutor
                </a>
                <a href="/" className="btn"
                  onClick={(e) => { e.preventDefault(); irA_ruta("/"); }}>
                  Buscar una tutoría
                </a>
              </div>
            </>
          ) : (
            <>
              <p className="lede">
                Ayudate le permite conectar con estudiantes que ya aprobaron el curso que se le está
                complicando. <b>Busca por nombre o código</b>, ve quién lo da y agenda.
              </p>
              <p className="frase">Nadie debería perder un curso por falta de quién le explique.</p>

              {/* Atajo al catálogo desde la portada: buscar es a lo que se
                  viene, y hacerlo desde arriba ahorra el recorrido por
                  institución y área. No reemplaza al buscador de la sección
                  —ese sigue con sus sugerencias, sus filtros y su listado—,
                  lo alimenta: al enviar, esto baja a #buscador y le pasa el
                  texto ya escrito. */}
              <form className="buscador" role="search" onSubmit={buscarDesdePortada}>
                <label htmlFor="q-portada">Curso o código</label>
                <div className="campo">
                  <input id="q-portada" name="curso" type="search" inputMode="search"
                    autoComplete="off" spellCheck={false} autoCapitalize="characters"
                    placeholder="MA1102, Cálculo, Química Básica…"
                    value={qPortada} onChange={(e) => setQPortada(e.target.value)} />
                  <button type="submit">Buscar</button>
                </div>
                <p className="hint">
                  <span className="num">{Object.keys(CURSO).length}</span> cursos del TEC y la UCR,
                  más idiomas, software, colegio y examen de admisión. Pruebe con{" "}
                  <code translate="no">MA1102</code> o con <code>Cálculo</code>.
                </p>
              </form>

              <div className="cta">
                <a href="#buscador" className="btn btn-p" onClick={(e) => irA(e, "buscador")}>
                  Buscar un tutor
                </a>
                <a href="/tutores" className="btn"
                  onClick={(e) => { e.preventDefault(); irA_ruta("/tutores"); }}>
                  Quiero dar tutorías
                </a>
              </div>
            </>
          )}
        </div>
        <div className="wrap">
          <nav className="cifras" aria-label="El catálogo en cifras">
            {CIFRAS.map(({ n, lb, ruta, ancla }) => (
              ancla && !esTutores ? (
                <a className="cifra" key={lb} href={`#${ancla}`} onClick={(e) => irA(e, ancla)}>
                  <b className="num">{n}</b><span>{lb}</span>
                </a>
              ) : ruta ? (
                <a className="cifra" key={lb} href={ruta}
                  onClick={(e) => { e.preventDefault(); irA_ruta(ruta); }}>
                  <b className="num">{n}</b><span>{lb}</span>
                </a>
              ) : (
                <div className="cifra" key={lb}><b className="num">{n}</b><span>{lb}</span></div>
              )
            ))}
          </nav>
        </div>
      </header>
      )}

      {esSuelta ? null : esTutores ? (
        <>
      <Pasos esTutores={esTutores} />

      <section className="sec" id="dar">
        <div className="wrap">
          <div className="eye">Para tutores</div>
          <h2 className="anton big">Gana con lo que ya sabe.</h2>
          <p className="sub">
            <b>Usted pone su horario.</b> Nosotros le conseguimos los estudiantes, coordinamos la sesión y
            le liquidamos lo que le corresponde.
          </p>
          <p className="sub" style={{ marginTop: 26 }}>
            <b>Qué pedimos:</b> comprobante de haber aprobado con nota igual o superior a <b>80</b>{" "}
            cada curso de universidad que vaya a impartir, correo institucional o personal y
            responder en menos de <b>24 horas</b>. En el caso de idiomas tener como mínimo B2; en
            software, el nivel de conocimiento que declare respaldado por certificado o experiencia
            laboral; y en preparación para el examen de admisión, su propio resultado del examen.{" "}
            <b>Para materias de colegio no pedimos nota ni comprobante.</b>
          </p>
        </div>
      </section>

      {/* Las tarifas viven acá, en la página de tutores, y no en la de
          estudiantes: la regla del ±10 % es una decisión que toma quien va a
          cobrar. El estudiante ve el precio ya calculado en cada tarjeta y no
          necesita la fórmula para agendar. */}
      <section className="sec" id="tarifas">
        <div className="wrap">
          <div className="eye">Tarifas</div>
          <h2 className="anton big">Usted pone el precio. Ayudate cobra ±10&nbsp;%.</h2>
          <p className="sub">
            No hay tarifa impuesta: cada tutor decide la suya por hora, en cualquier curso. Al
            estudiante se le cobra un <b>10&nbsp;% más</b> de esa tarifa y a usted se le descuenta
            un <b>10&nbsp;%</b> de la misma. El monto final se redondea a los <b>₡100</b> más
            cercanos.
          </p>
          <Reparto />
          <p className="sub" style={{ marginTop: 20 }}>
            El ejemplo es con una tarifa de {colones(TARIFA_SUGERIDA)} la hora. Esa comisión cubre
            la plataforma, la coordinación de cada sesión y la garantía de devolución. Si sube su
            tarifa, sube su ingreso: <b>no hay tope</b>. El mínimo son {colones(TARIFA_MINIMA)}.
          </p>
        </div>
      </section>
      <Politicas />
      <section className="sec" id="registro">
        <div className="wrap">
          <div className="eye">Registro</div>
          <h2 className="anton big">Regístrese como tutor.</h2>
          <div className="contacto">
            <div>
              <b>¿Prefiere preguntar antes de llenar el formulario?</b>
              <span>Escríbanos y le respondemos el mismo día.</span>
            </div>
            <div className="foot-links">
              <a className="fcanal fuerte" href={`https://wa.me/${CONTACTO.whatsapp}`}
                target="_blank" rel="noopener noreferrer">
                <i className="fic wa" aria-hidden="true" />
                WhatsApp {CONTACTO.whatsappVisible}
              </a>
              {CONTACTO.correo && (
                <a className="fcanal fuerte" href={`mailto:${CONTACTO.correo}`}>
                  <i className="fic mail" aria-hidden="true" />
                  {CONTACTO.correo}
                </a>
              )}
            </div>
          </div>

          <p className="datos">
            <b>Qué hacemos con sus datos:</b> los usamos únicamente para coordinar la tutoría y
            comunicarnos con usted. <b>No los compartimos ni los vendemos a nadie</b>, y puede pedirnos que
            los borremos cuando quiera.
          </p>

          <FormTutor />
        </div>
      </section>

        </>
      ) : (
        <>
      <Pasos esTutores={esTutores} />

      <section className="sec" id="buscador">
        <div className="wrap">
          <div className="eye">Buscador</div>
          <h2 className="anton big">Elija el área. Después el curso.</h2>
          <p className="sub">
            Empiece por el área de su curso y después elíjalo de la lista. Puede buscarlo <b>por su
            nombre o por su código</b>. Si no aparece, escríbanos por WhatsApp al{" "}
            {CONTACTO.whatsappVisible} o a {CONTACTO.correo} y lo agregamos.
          </p>
          <p className="datos">
            <b>Las tutorías son exclusivamente virtuales.</b> Las reuniones son creadas y
            gestionadas por la administración de Ayudate, que le hará llegar directamente el{" "}
            <b>enlace de acceso</b> para conectarse a la videollamada de cada sesión.
          </p>

          <CatalogoTutores tutores={tutores} cargando={cargando} errorCarga={errorCarga}
            onReintentar={cargar} perfilEstudiante={perfilEstudiante} consulta={consulta} />
        </div>
      </section>

      <Politicas />
        </>
      )}

      {esFaq && (
      <section className="sec" id="faq">
        <div className="wrap">
          <Faq />
        </div>
      </section>
      )}

      {esNosotros && (
      <section className="sec" id="nosotros">
        <div className="wrap">
          <div className="nos">
            <div>
              <p>
                Somos Javier y Gabo, dos estudiantes de Ingeniería Mecatrónica. Llevamos los mismos
                cursos filtro que se le complican a todo el mundo y sabemos lo que cuesta arrastrar
                uno: no es un semestre, es <b>la cadena entera de lo que viene detrás</b>.
              </p>
              <p>
                Ayudate nació de una pregunta simple: si en la universidad ya hay gente que aprobó
                ese curso con buena nota y podría explicarlo, ¿por qué encontrarla <b>depende de la
                suerte o de a quién conoce</b>?
              </p>
              <p>
                No somos una academia ni una empresa de cursos. Somos la conexión entre el que
                necesita ayuda y el compañero que ya pasó por ahí, con <b>reglas claras</b> para que
                ninguno de los dos quede mal parado.
              </p>
            </div>
            <div className="noscols">
              <div className="noscard">
                <span className="nosk">Producto · Operación · Desarrollo</span>
                <b>Javier Trujillo Soto</b>
                <p>Soy estudiante de último semestre de Ingeniería Mecatrónica. Disfruto mucho hacer
                  deporte como futbol, basket y ping pong. Cabe resaltar que soy adicto a entrenar en
                  el gimnasio escuchando música. Me apasiona hacer hiking para explorar la naturaleza
                  en busca de miradores mientras desconecto de la ciudad.</p>
              </div>
              <div className="noscard">
                <span className="nosk">Producto · Operación · Desarrollo</span>
                <b>Gabriel Mora Corrales</b>
                <p>Estudiante de Ingeniería Mecatrónica, en el último año de la carrera.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      </main>

      <footer className="foot">
        <div className="wrap">
          <div className="foot-top">
            <div>
              <div className="logo" style={{ marginBottom: 10 }}>
                <Marca />
              </div>
              <p className="foot-lb">Costa Rica · 2026</p>
            </div>
            <div className="foot-cont">
              <span className="foot-lb">¿Dudas antes de escribir? Contactanos directo</span>
              <div className="foot-links">
                <a className="fcanal" href={`https://wa.me/${CONTACTO.whatsapp}`}
                  target="_blank" rel="noopener noreferrer">
                  <i className="fic wa" aria-hidden="true" />
                  WhatsApp {CONTACTO.whatsappVisible}
                </a>
                {CONTACTO.correo && (
                  <a className="fcanal" href={`mailto:${CONTACTO.correo}`}>
                    <i className="fic mail" aria-hidden="true" />
                    {CONTACTO.correo}
                  </a>
                )}
              </div>
            </div>
          </div>
          <nav className="foot-nav" aria-label="Secciones del sitio">
            <a href="/" onClick={(e) => { e.preventDefault(); irA_ruta("/"); }}>Buscar un tutor</a>
            <a href="/tutores" onClick={(e) => { e.preventDefault(); irA_ruta("/tutores"); }}>
              Dar tutorías
            </a>
            <a href="/preguntas"
              onClick={(e) => { e.preventDefault(); irA_ruta("/preguntas", rutaActual); }}>
              Preguntas y respuestas
            </a>
            <a href="/nosotros"
              onClick={(e) => { e.preventDefault(); irA_ruta("/nosotros", rutaActual); }}>
              Quiénes somos
            </a>
          </nav>

          <p className="indep">
            Ayudate es un <b>proyecto independiente</b>. <b>No tiene relación oficial</b> con ninguna
            universidad ni con sus asociaciones estudiantiles, y no usa ningún logotipo. Los códigos
            y nombres de curso provienen de los planes de estudio públicos del TEC y de la UCR, y
            se citan solo como referencia.
          </p>
        </div>
      </footer>
      </div>

      <Accesibilidad ajustes={a11y} setAjustes={setA11y} />
    </div>
  );
}


/* ==================================================================
   EL CATÁLOGO EN CIFRAS
   Las tres primeras se cuentan de datos/areas.js, no van escritas a
   mano: agregar una carrera o un curso las mueve solas. La marquesina
   que había antes en este mismo lugar mostraba una cadena de códigos
   corriendo en bucle — bonita y muda; estas dicen el tamaño real del
   catálogo y llevan a él.
================================================================== */
const CIFRAS = [
  { n: Object.keys(CURSO).length, lb: "cursos en el catálogo", ancla: "buscador", ruta: "/" },
  { n: AREAS.length, lb: "áreas y carreras", ancla: "buscador", ruta: "/" },
  { n: INSTITUCIONES.length, lb: "instituciones y programas", ancla: "buscador", ruta: "/" },
  { n: "100 %", lb: "virtual, desde donde esté" },
];

/* ==================================================================
   CÓMO FUNCIONA
   Los tres pasos, con el texto que le toca a cada lado. Vivía duplicado
   palabra por palabra en las dos ramas del return —la de tutores y la de
   estudiantes— con el mismo condicional adentro de las dos copias.
================================================================== */
function Pasos({ esTutores }) {
  const PASOS = esTutores
    ? [["PASO 01", "Se registra", "Elige sus cursos, su tarifa y marca en qué horarios puede."],
       ["PASO 02", "Verificamos su perfil", "Le pedimos el comprobante de la nota. Sin eso, su perfil no se publica."],
       ["PASO 03", "Confirma y da la sesión", "Cuando alguien le hace una solicitud, le llega un correo: confirma con un clic y coordinamos el resto."]]
    : [["PASO 01", "Busca el curso", "Filtra por código. Ve quién lo da y a qué horas puede."],
       ["PASO 02", "El tutor confirma", "Le llega su solicitud por correo y responde si puede a esa hora. No se cobra nada todavía."],
       ["PASO 03", "Paga y se aparta", "Al confirmar el tutor, le llega un correo con la información para pagar por SINPE Móvil. Con el pago realizado, la sesión queda confirmada."]];
  return (
    <section className="sec" id="pasos">
      <div className="wrap">
        <div className="eye">Cómo funciona</div>
        <h2 className="anton big">Tres pasos, en ese orden.</h2>
        <div className="pasos">
          {PASOS.map(([k, t, d]) => (
            <div className="paso" key={k}>
              <span className="k">{k}</span>
              <h3>{t}</h3>
              <p>{d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ==================================================================
   EL REPARTO DE LA TARIFA
   La regla del ±10 % dibujada: de la tarifa del tutor salen dos ramas,
   una que sube al precio del estudiante y otra que baja a lo que recibe
   el tutor, y la diferencia queda para la plataforma. Es el diagrama de
   Demos/04-pizarra.html con su misma estructura, en la paleta de esta
   página.

   Los montos NO van escritos: salen de precioDe() (lib/tarifas.js), que
   es la misma función que calcula lo que se cobra de verdad. Si mañana
   la comisión cambia, el dibujo cambia con ella.

   El SVG entero es un role="img" con un aria-label que dice las tres
   cifras: quien no ve el dibujo recibe lo mismo, no un hueco. Los montos
   se repiten abajo en texto normal, que es donde se leen.
================================================================== */
function Reparto() {
  const pr = precioDe(TARIFA_SUGERIDA);
  const plataforma = pr.cliente - pr.tutor;
  return (
    <div className="reparto">
      <svg viewBox="0 0 620 152" role="img"
        aria-label={`Con una tarifa de ${colones(TARIFA_SUGERIDA)} la hora: el estudiante paga `
          + `${colones(pr.cliente)}, el tutor recibe ${colones(pr.tutor)} y `
          + `${colones(plataforma)} quedan para la plataforma.`}>
        <text className="rot" x="310" y="22" textAnchor="middle">
          Tarifa del tutor · {colones(TARIFA_SUGERIDA)} la hora
        </text>
        {/* el tronco, la llave que se abre y las dos puntas de flecha */}
        <path className="trazo" d="M310 34 L310 56" />
        <path className="trazo" d="M120 78 C 120 62, 130 60, 150 60 L 470 60 C 490 60, 500 62, 500 78" />
        <path className="trazo" d="M120 78 l -7 -9 M120 78 l 7 -9" />
        <path className="trazo" d="M500 78 l -7 -9 M500 78 l 7 -9" />
        <text className="pct sube" x="120" y="106" textAnchor="middle">+10&nbsp;%</text>
        <text className="quien" x="120" y="128" textAnchor="middle">al estudiante</text>
        <text className="pct baja" x="500" y="106" textAnchor="middle">−10&nbsp;%</text>
        <text className="quien" x="500" y="128" textAnchor="middle">al tutor</text>
        <text className="redondeo" x="310" y="104" textAnchor="middle">redondeado a ₡100</text>
      </svg>
      <div className="rlista">
        <div className="rc">
          <b>{colones(pr.cliente)}</b>
          <span>Paga el estudiante</span>
          <em>{TARIFA_SUGERIDA / 1000} mil + 10&nbsp;%</em>
        </div>
        <div className="rc destaca">
          <b>{colones(pr.tutor)}</b>
          <span>Recibe el tutor</span>
          <em>{TARIFA_SUGERIDA / 1000} mil − 10&nbsp;%</em>
        </div>
        <div className="rc">
          <b>{colones(plataforma)}</b>
          <span>Queda para la plataforma</span>
          <em>la diferencia</em>
        </div>
      </div>
    </div>
  );
}

/* ==================================================================
   POLÍTICAS
   Las cuatro, íntegras y numeradas: el número no es adorno, se cita en
   los correos y en el aviso que hay que aceptar antes de enviar
   cualquiera de los dos formularios. El título de la sección es el de
   siempre.

   Lo que cambió es la presentación: eran cuatro tarjetas en cuadrícula
   que se levantaban al pasar el puntero, y ahora van una debajo de otra
   separadas por una regla, como el documento que son. Estaba además
   duplicado palabra por palabra en las dos ramas del return, así que
   corregir una coma en las políticas eran dos ediciones y la
   oportunidad de que quedaran distintas.
================================================================== */
function Politicas() {
  /* Los porcentajes de devolución, con lo que significan en plata sobre
     el ejemplo. Los montos salen de EJ (lib/tarifas.js), no van escritos.
     `retiene` dice cómo se reparte lo que NO se devuelve: la leyenda de
     abajo es lo que explica esos dos tramos rayados de la barra. */
  const DEVOLUCIONES = [
    { cuando: "Cancelar la tutoría con más de 24 horas", pct: 80,
      monto: r100(EJ.cliente * 0.8), retiene: ["ay"] },
    { cuando: "Cancelar la tutoría con 24 horas o menos", pct: 60,
      monto: r100(EJ.cliente * 0.6), retiene: ["tu", "ay"] },
    { cuando: "El tutor no se presenta a la tutoría", pct: 100,
      monto: EJ.cliente, retiene: [] },
  ];

  return (
    <section className="sec" id="politicas">
      <div className="wrap">
        <div className="eye">Políticas</div>
        <h2 className="anton big">Las reglas, escritas antes de que hagan falta.</h2>
        <p className="sub">
          Cuatro políticas, en claro. Aplican desde la primera tutoría y se aceptan al enviar
          cualquiera de los dos formularios.
        </p>

        <div className="pols">

          {/* ---- 01 · CANCELACIÓN ---- */}
          <article className="pol">
            <header className="polh">
              <span className="pk">01</span>
              <h3>Cancelación y devolución</h3>
            </header>
            <p className="pold">
              Puede cancelar en cualquier momento, se le va a cobrar un <b>FEE</b> de acuerdo al
              siguiente gráfico. Los montos son los de una tutoría de {colones(EJ.cliente)} la hora.
            </p>

            <div className="barra">
              {DEVOLUCIONES.map(({ cuando, pct, monto, retiene }) => (
                <div className="bfila" key={cuando}>
                  <div className="bhead">
                    <span className="bcuando">{cuando}</span>
                    <span className="bmonto num">{pct}&nbsp;% · {colones(monto)}</span>
                  </div>
                  {/* La barra da la proporción y nada más: el porcentaje y el
                      monto ya están arriba, con su tamaño de lectura. El
                      aria-label la dice completa para quien no la ve. */}
                  <div className="btrack" role="img"
                    aria-label={`${cuando}: se devuelve el ${pct} por ciento, ${colones(monto)}.`}>
                    <div className={`bfill ${pct === 100 ? "full" : ""}`} style={{ width: `${pct}%` }} />
                    {retiene.map((q) => (
                      <div className={`bseg ${q}`} key={q} style={{ width: "20%" }} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="bleyenda">
              <span><i className="sw devuelto" />Se le devuelve</span>
              <span><i className="sw tu" />20&nbsp;% tutor</span>
              <span><i className="sw ay" />20&nbsp;% Ayudate</span>
            </div>

            <div className="cancel2" style={{ marginTop: 28 }}>
              <div className="cbox">
                <span className="ct">Para estudiantes</span>
                <ul>
                  <li>Cancelando con <b>más de 24 horas</b> de aviso, se le devuelve el <b>80&nbsp;%</b>.</li>
                  <li>Cancelando con <b>24 horas o menos</b>, se le devuelve el <b>60&nbsp;%</b>.</li>
                  <li>Si el tutor no se presenta, se le devuelve el <b>100&nbsp;%</b>.</li>
                </ul>
              </div>
              <div className="cbox">
                <span className="ct">Para tutores</span>
                <ul>
                  <li>Si el estudiante cancela con <b>más de 24 horas</b>, Ayudate retiene el
                    20&nbsp;% por la gestión y usted no recibe nada de esa sesión.</li>
                  <li>Si cancela con <b>24 horas o menos</b>, recibe un <b>20&nbsp;%</b> por el
                    horario que ya había bloqueado.</li>
                  <li>Si usted no se presenta, no recibe nada y se le devuelve todo al estudiante.</li>
                </ul>
              </div>
            </div>

            <p className="polnota">
              Los porcentajes son <b>siempre los mismos</b>; los montos dependen de la tarifa de cada
              tutor. Los del ejemplo corresponden a un tutor con una tarifa de{" "}
              {colones(TARIFA_SUGERIDA)} la hora, con lo que el estudiante paga {colones(EJ.cliente)}.
            </p>
          </article>

          {/* ---- 02 · SERVICIO ---- */}
          <article className="pol">
            <header className="polh">
              <span className="pk">02</span>
              <h3>Uso del servicio</h3>
            </header>
            <p className="pold">
              Ayudate existe para que aprenda el curso, <b>no para que alguien lo haga por usted</b>.
            </p>

            <div className="sino">
              <div className="col si">
                <span className="ct">Sí</span>
                <ul>
                  <li>Repasar temas antes de un parcial</li>
                  <li>Resolver ejercicios paso a paso</li>
                  <li>Entender lo que no quedó claro en clase</li>
                  <li>Prepararse para una reposición</li>
                </ul>
              </div>
              <div className="col no">
                <span className="ct">No</span>
                <ul>
                  <li>Comprar o vender tareas</li>
                  <li>Resolver exámenes por otra persona</li>
                  <li>Hacer proyectos o informes ajenos</li>
                  <li>Cualquier actividad evaluativa</li>
                </ul>
              </div>
            </div>

            <p className="polcita">
              El objetivo es promover el aprendizaje de manera <b>complementaria a la educación
              formal</b> impartida por las universidades y demás centros de estudio.
            </p>

            <div className="polalerta">
              <span className="polalerta-ic" aria-hidden="true">!</span>
              <div>
                <b>Expulsión permanente e inmediata</b>
                <span className="polalerta-desc">
                  Detectar una solicitud de este tipo implica la expulsión inmediata y permanente
                  de la plataforma, para el estudiante y para el tutor, sobre compra o venta de
                  tareas, exámenes u otra actividad evaluativa.
                </span>
              </div>
            </div>
          </article>

          {/* ---- 03 · TUTORES ---- */}
          <article className="pol">
            <header className="polh">
              <span className="pk">03</span>
              <h3>Tutores</h3>
            </header>
            <p className="pold">
              Nadie se publica solo. Cada perfil lo <b>revisa un administrador</b>.
            </p>

            {/* Los tres requisitos van en fila, no apilados: son datos
                comparables entre sí, no una secuencia. */}
            <div className="verif">
              <div className="nota80">
                <span className="n80" aria-hidden="true">80</span>
                <b>Nota mínima en universidad</b>
                <span className="nota80-desc">
                  Comprobante de haber aprobado, con nota igual o superior a 80, cada curso de
                  universidad que vaya a impartir.
                </span>
              </div>
              <div className="nota80">
                <span className="n80" aria-hidden="true">B2<sup>+</sup></span>
                <b>Nivel verificado en idiomas</b>
                <span className="nota80-desc">
                  Certificado del nivel declarado según el Marco Común Europeo. No se aceptan
                  niveles por debajo de B2.
                </span>
              </div>
              <div className="nota80">
                <span className="n80" aria-hidden="true">24&nbsp;h</span>
                <b>Respuesta comprometida</b>
                <span className="nota80-desc">
                  Correo vigente y respuesta a cada solicitud en menos de 24 horas.
                </span>
              </div>
            </div>

            <ul className="check">
              <li>En cursos de universidad: comprobante de nota igual o superior a <b>80</b></li>
              <li>En idiomas: certificado del nivel que declara, de <b>B2<sup>+</sup></b> (mínimo B2)</li>
              <li>En software: certificado, diploma o constancia laboral del nivel de
                conocimiento que declara</li>
              <li>En preparación de admisión: el resultado oficial del examen que hizo</li>
              <li>En materias de colegio: <b>no pedimos nota ni comprobante</b></li>
              <li>Correo institucional vigente</li>
              <li>Responder en <b>menos de 24 horas</b></li>
              <li>Un comprobante por cada curso, idioma o herramienta que lo pida</li>
            </ul>
            <p className="polnota">
              Sin el comprobante que le corresponda, el perfil <b>no se publica</b>. Se puede
              despublicar en cualquier momento si hay reclamos.
            </p>
          </article>

          {/* ---- 04 · CERO TOLERANCIA AL ACOSO ---- */}
          <article className="pol">
            <header className="polh">
              <span className="pk">04</span>
              <h3>Cero tolerancia al acoso</h3>
            </header>
            <p className="pold">
              Convivencia segura, <b>sin excepciones</b>, dentro y fuera de las sesiones virtuales.
            </p>

            <div className="polalerta">
              <span className="polalerta-ic" aria-hidden="true">!</span>
              <div>
                <b>Expulsión permanente e inmediata</b>
                <span className="polalerta-desc">
                  Cualquier caso de acoso, hostigamiento o conducta indebida expulsa
                  permanentemente de la plataforma tanto al estudiante como al tutor, sin excepción.
                </span>
              </div>
            </div>

            <div className="sino">
              <div className="col si">
                <span className="ct">Se espera</span>
                <ul>
                  <li>Trato respetuoso en cada sesión</li>
                  <li>Comunicación profesional, dentro y fuera de la videollamada</li>
                  <li>Reportar cualquier conducta indebida</li>
                </ul>
              </div>
              <div className="col no">
                <span className="ct">No se tolera</span>
                <ul>
                  <li>Acoso sexual o comentarios inapropiados</li>
                  <li>Hostigamiento o intimidación</li>
                </ul>
              </div>
            </div>

            <ul className="check">
              <li><b>Entorno seguro y respetuoso</b>, dentro y fuera de las sesiones virtuales</li>
              <li><b>Reporte directo a soporte</b>, cada incidente se revisa</li>
              <li><b>Notificación a las autoridades competentes</b>, si procede</li>
            </ul>

            <p className="polnota">
              Al usar la plataforma se compromete a mantener una <b>conducta profesional y
              respetuosa</b> en todo momento.
            </p>
          </article>

        </div>
      </div>
    </section>
  );
}
